import React, { useEffect, useMemo } from 'react';
import { Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import FilterDropdown from '../../FilterDropdown/FilterDropdown';
import FilterButton from '../../FilterButton/FilterButton';
import useFilterState from '../../../hooks/useFilterState';
import { filtersData } from '../../../constants/filtersData';
import { filterNames } from '../../../constants/filterNames';
import useHouses from '../../../queries/useHouses';
import FilterContainer from '../../FilterContainer/FilterContainer';
import SelectedOption from '../../SelectedOption/SelectedOption';
import { useFilterOptionsShorter } from '../../../hooks/useFilterOptionsShorter';
import { HiddenSelectedOptions } from '../../HiddenSelectedOptions/HiddenSelectedOptions';
import { transformHousesDataToStreets } from './transformHousesDataToStreets';
import { getValueByPath } from '../../../../../helpers/getValueByPath';
import useRequestServiceCompaniesStreets from '../../../../../queries/useRequestServiceCompaniesStreets';

const streetsFilter = filtersData[filterNames.streets];
const companiesFilter = filtersData[filterNames.companies];
const filter = filtersData[filterNames.houses];

const HousesFilter = ({ disabled = false }) => {
  const [streetsFilterState] = useFilterState(streetsFilter);
  const [filterState, setFilterState] = useFilterState(filter);

  const [companiesFilterState] = useFilterState(companiesFilter);

  const { data: streetsData } = useRequestServiceCompaniesStreets(
    companiesFilterState,
    {
      enabled: companiesFilterState.length > 0,
      staleTime: Infinity,
    }
  );

  const { data, isFetching } = useHouses(streetsFilterState);

  const filteredRequests = useSelector(
    (state) => state.statisticsStore.filteredRequests
  );

  const streetsMap = useMemo(() => {
    if (!streetsData) return new Map();
    return new Map(
      streetsData.map((street) => [street.id.toString(), street.nameWithPrefix])
    );
  }, [streetsData]);

  const tranformedItems = useMemo(() => {
    return transformHousesDataToStreets(data, streetsMap);
  }, [data, streetsMap]);

  const { itemsMap } = useMemo(() => {
    const map = new Map();

    tranformedItems.forEach((street) => {
      map.set(street.id, {
        ...street,
        amount: 0,
      });
    });

    if (filteredRequests?.length) {
      filteredRequests.forEach((request) => {
        const houseId = getValueByPath(request, 'houseId');
        if (houseId) {
          for (const street of tranformedItems) {
            const house = street.childrens?.find((h) => h.id === houseId);
            if (house) {
              const streetItem = map.get(street.id);
              if (streetItem) {
                map.set(street.id, {
                  ...streetItem,
                  amount: streetItem.amount + 1,
                });
              }
              break;
            }
          }
        }
      });
    }

    const items = Array.from(map.values()).filter((item) => item.amount !== 0);
    items.sort((a, b) => b.amount - a.amount);

    return { items, itemsMap: map };
  }, [tranformedItems, filteredRequests]);

  const tranformedItemsWithAmount = useMemo(() => {
    return tranformedItems
      .map((street) => {
        const houseAmounts = new Map();

        if (filteredRequests?.length) {
          filteredRequests.forEach((request) => {
            const houseId = getValueByPath(request, 'houseId');
            if (houseId) {
              const currentAmount = houseAmounts.get(houseId) || 0;
              houseAmounts.set(houseId, currentAmount + 1);
            }
          });
        }

        return {
          ...street,
          childrens:
            street.childrens
              ?.map((house) => ({
                ...house,
                amount: houseAmounts.get(house.id) || 0,
              }))
              .filter((house) => house.amount > 0) || [],
        };
      })
      .filter((street) => street.childrens.length > 0);
  }, [tranformedItems, filteredRequests]);

  const handleItemRemove = (id) => {
    const newState = filterState.filter((itemId) => itemId !== id);

    setFilterState(newState);
  };

  const hasFilter = filterState.length > 0;

  const allSelected = hasFilter && itemsMap.size === filterState.length;

  useEffect(() => {
    if (streetsFilterState.length === 0) {
      setFilterState([]);
    }
  }, [setFilterState, streetsFilterState]);

  const filteredItems = tranformedItemsWithAmount
    .flatMap((item) => item.childrens)
    .filter((item) => filterState.includes(item.id));
  const { visibleItems, hiddenItems } = useFilterOptionsShorter(filteredItems);

  return (
    <FilterContainer>
      <Tooltip title={streetsFilterState.length === 0 ? 'Выберите улицу' : ''}>
        <div>
          <FilterDropdown
            showCheckbox={true}
            disabled={isFetching || streetsFilterState.length === 0 || disabled}
            filter={filter}
            multiple
            items={tranformedItemsWithAmount}
            getItemContent={(item) => item.name}
            getSearchContent={(item) => item.name}
            renderButton={({ visible }) => (
              <FilterButton
                text="Дом"
                opened={visible}
                selected={hasFilter}
              />
            )}
          />
        </div>
      </Tooltip>
      {allSelected ? (
        <SelectedOption
          text="Все"
          onRemove={() => setFilterState([])}
        />
      ) : (
        <>
          {visibleItems.map((item) => (
            <SelectedOption
              key={item.id}
              text={item.name}
              value={item.id}
              onRemove={handleItemRemove}
            />
          ))}
          {Array.isArray(hiddenItems) && !!hiddenItems.length && (
            <HiddenSelectedOptions
              hiddenItems={hiddenItems}
              handleItemRemove={handleItemRemove}
            />
          )}
        </>
      )}
    </FilterContainer>
  );
};

export default HousesFilter;
